// 🔧 store_product_view.js
// ✅ Purpose: Public-facing Store product detail page
// ✅ Features:
// - Uses productSlug in the public URL instead of Firestore doc ID
// - Loads one active Store product from /events/{eventId}/store_items
// - Loads active variants under the selected product
// - Shows product image, description, price range, pickup/shipping info
// - Lets visitors choose a variant and quantity
// - Blocks purchase when variant is sold out
// - Lets shoppers add a selected option or continue to Merchant Cart checkout
// - Fully block-commented for clarity and future devs

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { Link, useParams } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { useNavigate } from "react-router-dom";
import { addStoreCartItem } from "../../utils/store_cart_helpers";
import SeoHead from "../../components/seo_head";
import { getPowerupPath, resolvePowerupSnapshot } from "../../utils/powerup_urls";

const StoreProductView = () => {

  const { productSlug } = useParams();
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cartNotice, setCartNotice] = useState("");
  const navigate = useNavigate();

  const centsToMoney = (cents) =>
    `$${(Number(cents || 0) / 100).toFixed(2)}`;

  useEffect(() => {
    if (!cartNotice) return undefined;

    const timer = setTimeout(() => setCartNotice(""), 20000);
    return () => clearTimeout(timer);
  }, [cartNotice]);

  useEffect(() => {
    const loadProduct = async () => {
      if (!eventId || !productSlug) return;

      setLoading(true);
      setNotFound(false);

      try {
        logger.log("🛒 Loading Store product by slug", {
          eventId,
          productSlug,
        });

        const productDoc = await resolvePowerupSnapshot(db, eventId, "product", productSlug);
        if (!productDoc?.exists()) {
          setProduct(null);
          setVariants([]);
          setNotFound(true);
          return;
        }

        const productData = {
          id: productDoc.id,
          ...productDoc.data(),
        };

        if (productData.active === false) {
          setProduct(null);
          setVariants([]);
          setNotFound(true);
          return;
        }

        const variantsSnap = await getDocs(
          query(
            collection(
              db,
              "events",
              eventId,
              "store_items",
              productDoc.id,
              "variants"
            ),
            orderBy("label", "asc")
          )
        );

        const activeVariants = variantsSnap.docs
          .map((variantDoc) => ({
            id: variantDoc.id,
            ...variantDoc.data(),
          }))
          .filter((variant) => variant.active !== false);

        setProduct(productData);
        setVariants(activeVariants);

        const firstAvailableVariant = activeVariants.find(
          (variant) => Number(variant.availableInventory || 0) > 0
        );

        setSelectedVariantId(firstAvailableVariant?.id || activeVariants[0]?.id || "");

        logger.log("✅ Store product loaded by slug", {
          eventId,
          productId: productDoc.id,
          variantCount: activeVariants.length,
        });
      } catch (err) {
        logger.error("❌ Error loading Store product by slug:", {
          value: err,
        });

        setProduct(null);
        setVariants([]);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [eventId, productSlug]);

  const selectedVariant = variants.find(
    (variant) => variant.id === selectedVariantId
  );

  const availableInventory = Number(
    selectedVariant?.availableInventory || 0
  );

  const quantityOptions = Array.from(
    {
      length: Math.max(Math.min(availableInventory, 10), 1),
    },
    (_, index) => index + 1
  );

  const getPriceLabel = () => {
    const prices = variants
      .map((variant) => Number(variant.priceCents || 0))
      .filter((price) => price > 0);

    if (prices.length === 0) return "Price not set";

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    return min === max
      ? centsToMoney(min)
      : `${centsToMoney(min)} - ${centsToMoney(max)}`;
  };

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store Product..." />;
  }

  if (notFound || !product) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="card shadow-sm border-0">
            <div className="card-body p-4 text-center">
              <h1 className="display-6 fw-bold">
                Product Not Found
              </h1>

              <p className="text-muted">
                This Store product is unavailable or has been removed.
              </p>

              <Link
                to={`/${eventslug}/store`}
                className="btn btn-outline-primary"
              >
                Back to Store
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const addSelectedItemToCart = () => {
    if (!product || !selectedVariant) return false;

    addStoreCartItem(eventslug, {
        productId: product.id,
          productSlug: product.productSlug,
        productName: product.name,
        productImageUrl: product.imageUrl || "",
        variantId: selectedVariant.id,
        variantLabel: selectedVariant.label,
        sku: selectedVariant.sku || "",
        priceCents: Number(selectedVariant.priceCents || 0),
        quantity: Number(quantity || 1),
        availableInventory,
        allowPickup: product.allowPickup === true,
        allowShipping: product.allowShipping === true,
    });

    return true;
  };

  const handleAddToCart = () => {
    if (addSelectedItemToCart()) {
      setCartNotice(`${selectedVariant.label} added to Merchant Cart.`);
    }
  };

  const handleBuyNow = () => {
    if (addSelectedItemToCart()) {
      navigate(`/${eventslug}/cart`);
    }
  };

    const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",

  name: product?.name,

  description:
    product?.description || undefined,

  image:
    product?.imageUrl
      ? [product.imageUrl]
      : undefined,
};

const productSeo = {
  title:
    `${product?.name || "Store Product"} | ${
      eventDoc?.name || "Event"
    }`,

  description:
    product?.description ||
    `Purchase ${
      product?.name || "this product"
    } from ${eventDoc?.name || "this event"}.`,

  path:
      getPowerupPath(eventslug, "product", product),

  image:
    product?.imageUrl ||
    eventDoc?.eventLogo,

  noindex: !isEventPublic(eventDoc),

  breadcrumbs: [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Store",
      path: `/${eventslug}/store`,
    },
    {
      name:
        product?.name || "Product",
      path: getPowerupPath(eventslug, "product", product),
    },
  ],

  structuredData: productSchema,
};

  return (
  <>
    <SeoHead {...productSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        {cartNotice && <div className="alert alert-success">{cartNotice}</div>}

        <div className="mb-4">
          <Link
            to={`/${eventslug}/store`}
            className="btn btn-outline-secondary btn-sm"
          >
            ← Back to Store
          </Link>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body p-4 p-lg-5">
            <div className="row g-4">
              <div className="col-12 col-lg-5">
                <div
                  className="d-flex align-items-center justify-content-center bg-light border rounded"
                  style={{
                    minHeight: "320px",
                  }}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name || "Store product"}
                      className="img-fluid"
                      style={{
                        maxHeight: "300px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <div className="text-muted">
                      No Image
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-lg-7">
                <h1 className="display-6 fw-bold mb-2">
                  {product.name || "Store Product"}
                </h1>

                <p className="lead text-muted">
                  {eventDoc?.name || "Event Store"}
                </p>

                <div className="mb-3">
                  {product.category && (
                    <span className="badge bg-secondary me-2">
                      {product.category}
                    </span>
                  )}

                  {product.featured && (
                    <span className="badge bg-warning text-dark">
                      Featured
                    </span>
                  )}
                </div>

                {product.description && (
                  <p className="text-muted">
                    {product.description}
                  </p>
                )}

                <div className="border rounded bg-light p-3 mb-4">
                  <div>
                    <strong>Pickup:</strong>{" "}
                    {product.allowPickup ? "Available" : "Not available"}
                  </div>

                  <div>
                    <strong>Shipping:</strong>{" "}
                    {product.allowShipping ? "Available" : "Not available"}
                  </div>
                </div>

                {variants.length === 0 ? (
                  <div className="alert alert-info">
                    This product does not have purchasable options yet.
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Select Option
                      </label>

                      <select
                        className="form-select"
                        value={selectedVariantId}
                        onChange={(e) => {
                          setSelectedVariantId(e.target.value);
                          setQuantity(1);
                        }}
                      >
                        {variants.map((variant) => {
                          const variantAvailable = Number(
                            variant.availableInventory || 0
                          );

                          return (
                            <option
                              key={variant.id}
                              value={variant.id}
                              disabled={variantAvailable <= 0}
                            >
                              {variant.label} — {centsToMoney(variant.priceCents)}
                              {variantAvailable > 0
                                ? ` — ${variantAvailable} available`
                                : " — Sold out"}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {selectedVariant && (
                      <div className="border rounded p-3 mb-3">
                        <div className="row g-2 small">
                          <div className="col-12 col-md-6">
                            <strong>SKU:</strong>{" "}
                            {selectedVariant.sku || "N/A"}
                          </div>

                          <div className="col-12 col-md-6">
                            <strong>Price:</strong>{" "}
                            {centsToMoney(selectedVariant.priceCents)}
                          </div>

                          <div className="col-12 col-md-6">
                            <strong>Size:</strong>{" "}
                            {selectedVariant.size || "N/A"}
                          </div>

                          <div className="col-12 col-md-6">
                            <strong>Fit:</strong>{" "}
                            {selectedVariant.fit || "N/A"}
                          </div>

                          <div className="col-12 col-md-6">
                            <strong>Color:</strong>{" "}
                            {selectedVariant.color || "N/A"}
                          </div>

                          <div className="col-12 col-md-6">
                            <strong>Quantity Available:</strong>{" "}
                            {availableInventory}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        Quantity
                      </label>

                      <select
                        className="form-select"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        disabled={!selectedVariant || availableInventory <= 0}
                      >
                        {quantityOptions.map((qty) => (
                          <option key={qty} value={qty}>
                            {qty}
                          </option>
                        ))}
                      </select>

                      <div className="form-text">
                        Quantity is limited by available inventory.
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!selectedVariant || availableInventory <= 0}
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        disabled={!selectedVariant || availableInventory <= 0}
                        onClick={handleAddToCart}
                      >
                        Add to Cart
                      </button>

                      <Link to={`/${eventslug}/cart`} className="btn btn-outline-dark">
                        Checkout
                      </Link>
                    </div>
                    
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
};

export default StoreProductView;
